import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import CmsGallerySection from "../components/CmsGallerySection";
import { FLEET_SPECS } from "../data/siteData";
import { imageUrl } from "../lib/images";

export default function KoeretoejerPage() {
  return (
    <div className="bg-[#070d18] text-slate-100 min-h-screen">
      <SEOHead
        title="Vores køretøjer"
        description="Udforsk KVC Express ApS' moderne vognpark af L2H2 diesel-kølevogne indrettet til professionel temperaturstyret erhvervstransport."
      />

      {/* Hero Header */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 border-b border-slate-800 bg-[#081224] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
              <i className="fa-solid fa-truck-front text-[10px]" aria-hidden="true" />
              <span>Vognpark · Investering i Materiel</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight font-display">
              Nye biler. Stabil drift. Professionel levering.
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Vores køretøjer er et centralt fundament for virksomheden. Vores flåde består af nye diesel- og elbiler med aktiv køl og præcis temperaturstyring. Vi leverer i hele Danmark – inklusive Bornholm.
            </p>
          </div>
        </div>
      </section>

      {/* Technical Overview & Visual Cards */}
      <section className="py-20 bg-slate-100 text-slate-900 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Spec Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-16">
            {FLEET_SPECS.map((spec, i) => (
              <div
                key={i}
                className="p-5 rounded-xl bg-white border border-slate-200/90 shadow-sm flex flex-col justify-between"
              >
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                  {spec.label}
                </span>
                <span className="text-base font-bold text-slate-950 mt-2">
                  {spec.value}
                </span>
              </div>
            ))}
          </div>

          {/* Editorial Double Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0F766E] font-bold">
                Materiel & Chaufførkompetencer
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-950 tracking-tight font-display">
                L2H2 formatet: Fleksibilitet, kapacitet og professionelle førere
              </h2>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Vores valg af L2H2 varevogne giver den ideelle balance mellem kapacitet og manøvredygtighed. Bilerne kan ubesværet navigere i travle erhvervsområder, snævre læsseramper og tætte bymiljøer i hele Danmark – inklusive Bornholm.
              </p>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Vores flåde består af nye diesel- og elbiler, så vi kan tilbyde både fleksibel rækkevidde, høj driftssikkerhed og moderne, emissionsreduceret logistik.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-sm">
                  <div className="text-[#0F766E] font-bold text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-charging-station text-[#0F766E]" aria-hidden="true" />
                    <span>Nye diesel- og elbiler</span>
                  </div>
                  <div className="text-xs text-slate-600">
                    Moderne vognpark sammensat af nye diesel- og elbiler med aktiv temperaturkontrol.
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-sm">
                  <div className="text-[#0F766E] font-bold text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-id-card-clip text-[#0F766E]" aria-hidden="true" />
                    <span>Uddannede Chauffører</span>
                  </div>
                  <div className="text-xs text-slate-600">
                    Chaufføruddannelsesbevis samt kurser i transport af køle- og medicinalvarer.
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden border border-slate-300 bg-white shadow-xl">
                <img
                  src={imageUrl("/_assets/gordon-elfordon425-3.jpg", "width=900,quality=85,format=auto")}
                  alt="KVC Express moderne el- og dieselkøretøjer til by- og landsdækkende distribution"
                  className="w-full h-80 sm:h-[450px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <CmsGallerySection
        gallerySlug="koeretoejer"
        title="Flådegalleri"
        subtitle="Fotografisk dokumentation af vores moderne kølevogne og transportmateriel"
      />

      {/* Bottom CTA */}
      <section className="py-16 bg-[#050a14] border-t border-slate-800 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-white font-display">
            Vil I vide mere om vores materiel og ledige kapacitet?
          </h3>
          <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
            Vi tager gerne en snak om, hvordan vores biler kan indgå som en fast del af jeres logistikløsning.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#0F766E] hover:bg-[#115E59] active:bg-[#134E4A] text-white font-bold text-xs uppercase tracking-wider transition shadow-xl shadow-[#0F766E]/40"
          >
            <span>Drøft et samarbejde</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
